import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { contactRoles, personObj, fields } from '../../../constants';
import { ProfileContext } from '../../../context/Profile.context';
import { personEntityTitle } from '../../../utils/helper';
import { isDirectorComplete } from '../../../utils/completes';
import Controls from '../../Controls';
import DirectorForm from './DirectorForm';
import PreviousAdded from '../../PreviousAdded';
import RemovePerson from '../../RemovePerson';

import { Lock, User, UserCheck, UserLock, Trash2 } from 'lucide-react';
import { Box, Button, Dialog, DialogContent, DialogTitle, EntityLabelGroup, EntityLabel, Label, TextField, Typography, InfoPanel, SectionTitle, Divider } from '@otcmarketsgroup/otcm-ui-lib';


const PREV_ADDED_VIEW = 'previousAdded';
const FORM_VIEW = 'formView';
const REMOVE_PERSON_VIEW = 'removePerson';
const isDirector = fields.DIRECTOR;

const Directors = ({ onPersonAdd, onPersonUpdate, onPersonRemove }) => {
  const [profileState, dispatchProfile] = useContext(ProfileContext);
  const [dialog, setDialog] = useState(false);
  const [directorView, setDirectorView] = useState(personObj);
  const [listFocus, setListFocus] = useState(false);
  const [viewHistory, setViewHistory] = useState(null);
  const peopleList = profileState.profile.peopleList;
  const directorList = peopleList && peopleList.filter(person => person[isDirector] && !person.isChairPerson);
  const officerList = peopleList && peopleList.filter(person => person.isOfficer && !person[isDirector]);
  const previousAddView = officerList && officerList.length > 0;
  const [slideView, setSlideView] = useState(previousAddView ? PREV_ADDED_VIEW : FORM_VIEW);
  const isReadOnly = profileState.readOnly;
  const isAdd = !directorView.id || directorView && !directorView[isDirector];
  const addTitle = 'Add Director';
  const isRemoveView = slideView === REMOVE_PERSON_VIEW;
  const dialogTitle = isRemoveView ? `Remove - ${personEntityTitle(directorView, false)}` : isAdd ? addTitle : personEntityTitle(directorView, false);

  useEffect(() => {
    if (!dialog) {
      setDirectorView(personObj);
      setSlideView(previousAddView ? PREV_ADDED_VIEW : FORM_VIEW);
    }
  }, [dialog, previousAddView]);

  const onValueChanged = (person) => {
    setDirectorView(person);
  };

  const handleCancelRemove = _ => {
    setDialog(false);
  };

  const handleEdit = person => {
    setSlideView(FORM_VIEW);
    setDirectorView({ ...person });
    setDialog(true);
  };

  const handlePersonAdd = person => {
    onPersonAdd(person);
    setDialog(false);
  };

  const handlePersonUpdate = person => {
    onPersonUpdate(person);
    setDialog(false);
  };

  const handleCancel = _ => {
    viewHistory === PREV_ADDED_VIEW && officerList && officerList.length > 0 ? setSlideView(PREV_ADDED_VIEW) : setDialog(false);
  };

  const handleSelectOfficer = person => {
    const newOfficer = person;
    setViewHistory(PREV_ADDED_VIEW);
    setDirectorView(newOfficer);
    setSlideView(FORM_VIEW);
  };

  const handleRemove = (e, person) => {
    e.stopPropagation();
    onPersonRemove(person, isDirector);
  };

  const handleRemoveClick = (e, person) => {
    // e.stopPropagation();
    setDirectorView(person);
    setSlideView(REMOVE_PERSON_VIEW);
    setDialog(true);
  };

  const isCustomRemove = person => {
    const totalRoles = contactRoles.filter(role => person[role]);

    return totalRoles.length > 1;
  };

  const onRemoveSubmit = person => {
    delete person.isIndependent;
    delete person.isAuditCommittee;
    delete person.isNominatingCommittee;
    delete person.isCompensationCommittee;

    onPersonUpdate(person);
    setDialog(false);
  };

  const handleAddNew = _ => {
    setViewHistory(PREV_ADDED_VIEW);
    setSlideView(FORM_VIEW);
  };

  const validateComplete = person => {
    return isDirectorComplete(person);
  };

  const renderPreviouslyAdded = _ => (
    <div>
      <p>
        Select individual previously entered as an Officer/Contact or add a new individual below
      </p>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <PreviousAdded
          listTitle='All Officers or Contact Previously Added'
          items={officerList}
          handleSelect={handleSelectOfficer} />
        <Divider />
        <Button
          fullWidth
          onClick={handleAddNew}>
          Add New Director
        </Button>
      </Box>
    </div>
  );

  const dialogClose = _ => {
    setDialog(false);
  };

  return (
    <div className='mtXL'
      onMouseEnter={() => setListFocus(true)}
      onMouseLeave={() => setListFocus(false)}>
      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <SectionTitle noUnderline>
          Directors
        </SectionTitle>
        {!isReadOnly && <Button variant='text' onClick={() => setDialog(true)}>
          Add Director
        </Button>}
      </Box>
      {!directorList || directorList.length === 0 && <Typography variant='body2'>
        No Directors have been added yet.
      </Typography>}
      <EntityLabelGroup>
        {directorList.map((director, i) => {
          const isComplete = validateComplete(director);

          return (
            <EntityLabel
              key={i}
              title={personEntityTitle(director, false)}
              subtitle={isComplete ? 'Complete' : 'Enter Information'}
              icon={isComplete ? <UserCheck /> : <User />}
              actionIcon={!isReadOnly ? <Trash2 /> : undefined}
              isComplete={isComplete}
              isWarning={!isComplete}
              onEntityClick={() => handleEdit(director)}
              onActionClick={!isReadOnly ? (e) => handleRemoveClick(e, director) : undefined} />
          );
        })}
      </EntityLabelGroup>
      <Dialog open={dialog} onClose={dialogClose}>
        <DialogTitle title={dialogTitle} onClose={dialogClose} />
        <DialogContent>
          {slideView === PREV_ADDED_VIEW && renderPreviouslyAdded()}
          {slideView === FORM_VIEW && <DirectorForm
            person={{ ...directorView }}
            isAdd={isAdd}
            isReadOnly={isReadOnly}
            onCancel={handleCancel}
            onPersonAdd={handlePersonAdd}
            onPersonUpdate={handlePersonUpdate}
            onValueChanged={onValueChanged} />}
          {isRemoveView && <RemovePerson
            person={{ ...directorView }}
            selectedRemove={isDirector}
            onCancel={handleCancelRemove}
            onRemoveSubmit={onRemoveSubmit} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

Directors.propTypes = {
  onPersonAdd: PropTypes.func,
  onPersonUpdate: PropTypes.func,
  onPersonRemove: PropTypes.func
};


export default Directors;
